import { Page, PageTitle } from "@/components/page"
import { CRUDLoader } from "@/components/page/crudloader"
import { CRUDToolbar } from "@/components/page/crudtoolbar"
import { useDatabaseData, useDatabaseItem, usePage } from "@/components/page/hooks"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Combobox } from "@/components/ui/combobox"
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { supabase } from "@/lib/data"
import { zodResolver } from "@hookform/resolvers/zod"
import { CircleX, Radio } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { useOpenRecordContext, useReloadOnAssistantChange } from "@/lib/assistant/modules/channels"
import { useForm } from "react-hook-form"
import { z } from "zod"

export const channels_crud = {
  title: "Channel",
  route: "/settings/channels/:id",
  component,
  group: "settings",
  resource: "apikeys",
  icon: Radio,
  action: "read",
} satisfies PageConfig

const FormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  type: z.enum(["telerivet", "whatsapp", "messenger", "instagram", "telegram"], { required_error: "Type is required", invalid_type_error: "Type is required" }),
  agent: z.coerce.number({ required_error: "Agent is required", invalid_type_error: "Agent is required" }),
  debounce_time: z.coerce.number().nullable().optional(),
  debounce_type: z.enum(["none", "debounce", "singleAgentInstance"]).optional(),
  debug: z.boolean().nullable().optional(),
  evaluations: z.array(z.number()).nullable().optional(),
  telerivet_apikey: z.string().nullable().optional(),
  telerivet_projectid: z.string().nullable().optional(),
  telerivet_routeid: z.string().nullable().optional(),
  whatsapp_phoneid: z.string().nullable().optional(),
  whatsapp_token: z.string().nullable().optional(),
  answer_via_whatsapp: z.boolean().optional(),
  settings: z.object({
    page_id: z.string().nullable().optional(),
    page_token: z.string().nullable().optional(),
    bot_token: z.string().nullable().optional(),
  }).nullable().optional(),
}).refine((data) => data.debounce_type !== "debounce" || (data.debounce_time != null && data.debounce_time > 0), {
  message: "Debounce time is required",
  path: ["debounce_time"],
}).refine((data) => data.type !== "telerivet" || !!data.telerivet_apikey, {
  message: "Telerivet API Key is required",
  path: ["telerivet_apikey"],
}).refine((data) => {
  const requiresWhatsapp = data.type === "whatsapp" || (data.type === "telerivet" && data.answer_via_whatsapp)
  return !requiresWhatsapp || !!data.whatsapp_token
}, {
  message: "Whatsapp Token is required",
  path: ["whatsapp_token"],
}).refine((data) => !isMeta(data.type) || !!data.settings?.page_token, {
  message: "Page Access Token is required",
  path: ["settings", "page_token"],
}).refine((data) => data.type !== "telegram" || !!data.settings?.bot_token, {
  message: "Bot Token is required",
  path: ["settings", "bot_token"],
})

// Messenger and Instagram DMs are both answered through the linked Facebook Page.
function isMeta(type: string) {
  return type === "messenger" || type === "instagram"
}

type FormData = z.infer<typeof FormSchema>

export function component() {
  const { id } = usePage()
  const version = useReloadOnAssistantChange("channels", id, "/settings/channels")
  return <ChannelEditor key={version} />
}

function ChannelEditor() {

  const { team, id } = usePage()
  const { data, loading, submit, toastSuccess, navigate } = useDatabaseItem("channels")
  const { data: agentsData, loading: agentsLoading } = useDatabaseData("agents", { fields: ["id", "title", "team_id"] })

  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: data as FormData,
    values: data as FormData,
  })

  const type = form.watch("type")
  const debounceType = form.watch("debounce_type")
  const answerViaWhatsapp = form.watch("answer_via_whatsapp")
  const showWhatsapp = type === "whatsapp" || (type === "telerivet" && answerViaWhatsapp)

  useOpenRecordContext("channels", id, () => form.getValues("title"), () => form.formState.isDirty)

  async function onSubmit(data: FormData) {
    const payload = { ...data, team: team?.id }
    await submit(payload as any)
    toastSuccess()
    navigate("/settings/channels")
  }

  if (loading || agentsLoading) return <CRUDLoader />

  const agentOptions = (agentsData || [])
    .filter((a: any) => a.team_id === team?.id)
    .map((a: any) => ({ value: a.id, label: a.title }))

  return <Page>
    <div className="pb-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <PageTitle>
            <CRUDToolbar table="channels" onSuccess="/settings/channels" />
          </PageTitle>
          <div className="rgrid">
            <div className="row">
              <div className="rc-6">
                <FormField control={form.control} name="title" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-6">
                <FormField control={form.control} name="type" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger><SelectValue placeholder="Select a type" /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="telerivet">Telerivet</SelectItem>
                        <SelectItem value="whatsapp">Whatsapp</SelectItem>
                        <SelectItem value="messenger">Facebook Messenger</SelectItem>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="telegram">Telegram</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>

            <div className="row">
              <div className="rc-4">
                <FormField control={form.control} name="agent" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Agent</FormLabel>
                    <FormControl>
                      <Combobox items={agentOptions} value={field.value} onChange={field.onChange} placeholder="Search agent..." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-4">
                <FormField control={form.control} name="debounce_type" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Debounce Type</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger><SelectValue placeholder="Select debounce type" /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="debounce">Debounce</SelectItem>
                        <SelectItem value="singleAgentInstance">Single Agent Instance</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              {debounceType === "debounce" && (
                <div className="rc-2">
                  <FormField control={form.control} name="debounce_time" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Debounce Time (s)</FormLabel>
                      <FormControl><Input type="number" {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
              )}
              <div className="rc-2">
                <FormField control={form.control} name="debug" render={({ field }) => (
                  <FormItem className="flex items-center gap-2 pt-8 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="mt-0!">Debug</FormLabel>
                  </FormItem>
                )} />
              </div>
            </div>

            {type === "telerivet" && (
              <div className="row">
                <div className="rc-4">
                  <FormField control={form.control} name="telerivet_apikey" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telerivet API Key</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <div className="rc-4">
                  <FormField control={form.control} name="telerivet_projectid" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telerivet Project ID</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <div className="rc-4">
                  <FormField control={form.control} name="telerivet_routeid" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telerivet Route ID</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <div className="rc-12">
                  <FormField control={form.control} name="answer_via_whatsapp" render={({ field }) => (
                    <FormItem className="flex items-center gap-2 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="mt-0!">Answer via Whatsapp</FormLabel>
                    </FormItem>
                  )} />
                </div>
              </div>
            )}

            {isMeta(type) && (
              <div className="row">
                <div className="rc-6">
                  <FormField control={form.control} name="settings.page_id" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Page ID</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormDescription>Optional when a Router points here: the webhook provides it.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <div className="rc-6">
                  <FormField control={form.control} name="settings.page_token" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Page Access Token</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
              </div>
            )}

            {type === "telegram" && (
              <div className="row">
                <div className="rc-6">
                  <FormField control={form.control} name="settings.bot_token" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bot Token</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
              </div>
            )}

            {showWhatsapp && (
              <div className="row">
                <div className="rc-6">
                  <FormField control={form.control} name="whatsapp_phoneid" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Whatsapp Phone ID</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormDescription>Optional when a Router points here: the webhook provides it.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <div className="rc-6">
                  <FormField control={form.control} name="whatsapp_token" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Whatsapp Token</FormLabel>
                      <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
              </div>
            )}

            <div className="row">
              <div className="rc-12">
                <FormField control={form.control} name="evaluations" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Evaluations</FormLabel>
                    <FormControl>
                      <EvaluationsPicker value={field.value || []} onChange={field.onChange} team={team?.id} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  </Page>

}

function EvaluationsPicker({ value, onChange, team }: { value: number[], onChange: (v: number[]) => void, team: string }) {

  const [evalItems, setEvalItems] = useState<{ id: number; name: string }[]>([])
  const [comboValue, setComboValue] = useState<any>("")
  const loadedRef = useRef(false)

  useEffect(() => {
    if (loadedRef.current) return
    loadedRef.current = true
    supabase.from("eval_items").select("id, name").eq("team", team).then(({ data }) => {
      if (data) setEvalItems(data as any)
    })
  }, [team])

  const selectedItems = evalItems.filter((i) => value.includes(i.id))
  const unselected = evalItems.filter((i) => !value.includes(i.id))
  const comboItems = [
    ...(unselected.length > 0 ? [{ value: "__add_all__", label: "<Add All>" }] : []),
    ...unselected.map((i) => ({ value: i.id, label: i.name }))
  ]

  function onSelectItem(v: any) {
    setComboValue("")
    if (!v) return
    if (v === "__add_all__") { onChange([...value, ...unselected.map((i) => i.id)]); return }
    if (value.includes(v)) return
    onChange([...value, v])
  }

  function onRemoveItem(id: number) {
    onChange(value.filter((i) => i !== id))
  }

  return <div className="flex flex-col gap-4">
    <Combobox items={comboItems} value={comboValue} onChange={onSelectItem} placeholder="Search evaluation items..." />
    <ScrollArea className="h-48 rounded-md border w-full">
      <div className="p-2">
        {selectedItems.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No items selected</p>}
        {selectedItems.map((item) => (
          <div key={item.id}>
            <div className="flex items-center justify-between py-2 px-2">
              <span className="text-sm">{item.name}</span>
              <CircleX className="text-red-500 cursor-pointer shrink-0 ml-4" size={16} onClick={() => onRemoveItem(item.id)} />
            </div>
            <Separator />
          </div>
        ))}
      </div>
    </ScrollArea>
    {selectedItems.length > 0 && <Button type="button" variant="outline" size="sm" className="w-full text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => onChange([])}>Remove All</Button>}
  </div>

}
